import {
	DEFAULT_CONTACT_BUTTON_LABEL,
	DEFAULT_LOCATION_BUTTON_LABEL,
	INPUT_VALIDATIONS,
	type InputValidation,
	MAX_BUTTON_LABEL_LENGTH,
	MAX_INPUT_RETRIES,
	MAX_QUICK_REPLIES,
	MEDIA_VALIDATIONS,
} from "@velizhanin-dev/bot-scenario"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import { TextWithVariables } from "@/shared/ui/text-with-variables"
import { VariableSelect } from "@/shared/ui/variable-select"
import type { Config, EditorProps } from "../editor-props"
import { parseOptionalNumber, readNumber, readString, withField } from "../editor-props"
import { issuesAt } from "../issues-text"
import { SendOptionsFields } from "../send-options-fields"
import { StringListField } from "../string-list-field"
import { TimeoutField } from "../timeout-field"

const VALIDATION_LABELS: Record<InputValidation, string> = {
	text: "Любой текст",
	email: "Email",
	phone: "Телефон текстом",
	number: "Число",
	contact: "Контакт кнопкой (или телефон текстом)",
	location: "Геолокация",
	photo: "Фото",
	video: "Видео или кружок",
	document: "Файл-документ",
	voice: "Голосовое или аудио",
	file: "Любой файл",
}

export function QuestionEditor({ config, onChange, issues, draft }: EditorProps) {
	const validation = (readString(config, "validation") || "text") as InputValidation
	const withRange = validation === "text" || validation === "number"
	const requestButton = validation === "contact" || validation === "location"
	const withQuickReplies = !MEDIA_VALIDATIONS.has(validation) && validation !== "location"
	const quickReplies = Array.isArray(config.quickReplies) ? (config.quickReplies as string[]) : []

	const setValidation = (next: InputValidation) => {
		let base: Config = { ...config, validation: next }
		if (next !== "text" && next !== "number") {
			base = withField(withField(base, "min", undefined), "max", undefined)
		}
		if (next !== "contact" && next !== "location")
			base = withField(base, "requestButtonLabel", undefined)
		if (MEDIA_VALIDATIONS.has(next) || next === "location")
			base = withField(base, "quickReplies", undefined)
		onChange(base)
	}

	return (
		<div className="space-y-4">
			<Field label="Вопрос" htmlFor="question-text" issues={issuesAt(issues, "text")}>
				<TextWithVariables
					rich="telegram-html"
					id="question-text"
					value={readString(config, "text")}
					onChange={(text) => onChange({ ...config, text })}
					variables={draft.variables}
				/>
			</Field>
			<Field label="Какой ответ принимать" issues={issuesAt(issues, "validation")}>
				<NativeSelect
					aria-label="Проверка ответа"
					value={validation}
					onChange={(event) => setValidation(event.target.value as InputValidation)}
				>
					{INPUT_VALIDATIONS.map((value) => (
						<option key={value} value={value}>
							{VALIDATION_LABELS[value]}
						</option>
					))}
				</NativeSelect>
			</Field>
			<Field
				label="Сохранить ответ в"
				issues={issuesAt(issues, "variable")}
				hint={
					draft.variables.length === 0
						? "Сначала объявите переменную в «Переменных»"
						: MEDIA_VALIDATIONS.has(validation)
							? "Файл сохранится как «вид:file_id»: подойдёт переменная типа «Файл»"
							: undefined
				}
			>
				<VariableSelect
					aria-label="Переменная для ответа"
					value={readString(config, "variable")}
					onChange={(variable) => onChange({ ...config, variable })}
					variables={draft.variables}
				/>
			</Field>
			{withRange ? (
				<div className="grid grid-cols-2 gap-2">
					{(["min", "max"] as const).map((key) => (
						<Field
							key={key}
							label={`${key === "min" ? "Минимум" : "Максимум"}${validation === "text" ? " символов" : ""}`}
							issues={issuesAt(issues, key)}
						>
							<Input
								type="number"
								aria-label={key}
								placeholder="нет"
								value={readNumber(config, key) ?? ""}
								onChange={(event) =>
									onChange(withField(config, key, parseOptionalNumber(event.target.value)))
								}
							/>
						</Field>
					))}
				</div>
			) : null}
			{requestButton ? (
				<Field label="Подпись кнопки" hint="Кнопка заменит клавиатуру телефона">
					<Input
						aria-label="Подпись кнопки запроса"
						maxLength={MAX_BUTTON_LABEL_LENGTH}
						placeholder={
							validation === "contact"
								? DEFAULT_CONTACT_BUTTON_LABEL
								: DEFAULT_LOCATION_BUTTON_LABEL
						}
						value={readString(config, "requestButtonLabel")}
						onChange={(event) =>
							onChange(withField(config, "requestButtonLabel", event.target.value || undefined))
						}
					/>
				</Field>
			) : null}
			{withQuickReplies ? (
				<Field
					label="Готовые ответы"
					hint="Кнопки вместо клавиатуры; ответить своим текстом тоже можно"
					issues={issuesAt(issues, "quickReplies", true)}
				>
					<StringListField
						values={quickReplies}
						onChange={(next) =>
							onChange(withField(config, "quickReplies", next.length ? next : undefined))
						}
						placeholder="Например, Москва"
						max={MAX_QUICK_REPLIES}
						itemLabel="ответ"
					/>
				</Field>
			) : null}
			<Field
				label="Если ответ не подошёл"
				hint="Пусто: стандартный текст «Не получилось разобрать ответ»"
				issues={issuesAt(issues, "errorText")}
			>
				<TextWithVariables
					rich="telegram-html"
					value={readString(config, "errorText")}
					onChange={(errorText) => onChange({ ...config, errorText })}
					variables={draft.variables}
					rows={2}
				/>
			</Field>
			<Field
				label="Сколько раз переспросить"
				hint="Потом бот идёт по выходу «Попытки кончились», а если он не подключён, то по «Не понял»"
				issues={issuesAt(issues, "maxRetries")}
			>
				<NativeSelect
					aria-label="Число повторов"
					value={readNumber(config, "maxRetries") ?? 2}
					onChange={(event) => onChange({ ...config, maxRetries: Number(event.target.value) })}
				>
					{Array.from({ length: MAX_INPUT_RETRIES + 1 }, (_, value) => (
						<option key={value} value={value}>
							{value}
						</option>
					))}
				</NativeSelect>
			</Field>
			<TimeoutField config={config} onChange={onChange} what="ответа" />
			<SendOptionsFields config={config} onChange={onChange} />
		</div>
	)
}
